import { NotificationPriority, NotificationType } from '@shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_NOTIFICATIONS_KEY } from '@web-app/hooks/consts';

export interface Notification {
  id: string;
  providerHealthieId: number;
  type: NotificationType;
  title: string;
  message: string | null;
  area: string[];
  priority: NotificationPriority;
  route: string;
  viewed: boolean;
  viewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NotificationsResponse {
  notifications: Notification[];
}

const fetchNotifications = async (): Promise<Notification[]> => {
  const response = await fetch('/api/notifications');

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch notifications');
  }

  const data: NotificationsResponse = await response.json();
  return data.notifications;
};

const markNotificationAsViewed = async (id: string): Promise<void> => {
  const response = await fetch(`/api/notifications/${id}/viewed`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to mark notification as viewed');
  }
};

// Returns notifications where viewed: false
export const useNotifications = () => {
  return useQuery<Notification[], Error>({
    queryKey: [QUERY_NOTIFICATIONS_KEY],
    queryFn: fetchNotifications,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

// Sets viewed: true for notification
export const useMarkNotificationViewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationAsViewed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_NOTIFICATIONS_KEY] });
    },
  });
};
